// deps
import React, { memo, useCallback } from "react";
import { ReactElement } from "react";

// internal-deps
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { UserAddress } from "@swiggy-private/cart-types";

// constants
import { BHARAT_MANDAPAM_TEXTS, BHARAT_MANDAPAM_IMAGE } from "./constants";
import { getPositionFromBottomForAddressList } from "@cart-ui/helpers/CartFooter";

// styles
import {
    ConfirmContainer,
    ConfirmImageContainer,
    PickupPointCard,
    PickupPointNameRow,
    NearestBadge,
    ConfirmButtonContainer,
    FullWidthSuperButton,
    ConfirmSubheading,
    ConfirmTitleContainer,
    PickupPointInfoCenter,
} from "./styles";

export interface BharatMandapamConfirmPickupProps {
    isOpen: boolean;
    onClose: VoidFunction;
    onConfirm: VoidFunction;
    onChangePickupPoint: VoidFunction;
    selectedAddress: UserAddress | null;
}

const BharatMandapamConfirmPickupContent = memo(function BharatMandapamConfirmPickupContent({
    onConfirm,
    onChangePickupPoint,
    selectedAddress,
}: Omit<BharatMandapamConfirmPickupProps, "isOpen" | "onClose">): ReactElement {
    const pickupPointName = selectedAddress?.annotation || `${BHARAT_MANDAPAM_TEXTS.PICKUP_POINT_LABEL} 1`;
    // const pickupPointAddress = selectedAddress?.address || "";

    return (
        <ConfirmContainer>
            <ConfirmImageContainer>
                <Image
                    cloudinaryId={BHARAT_MANDAPAM_IMAGE.CLOUDINARY_ID}
                    height={`${BHARAT_MANDAPAM_IMAGE.HEIGHT}px`}
                    cloudinaryHeight={BHARAT_MANDAPAM_IMAGE.HEIGHT * 2}
                    fit="cover"
                    lazyLoad={false}
                />
            </ConfirmImageContainer>

            <ConfirmTitleContainer>
                <ConfirmSubheading variant="V2.body2.neutral.regular" color="text_Low_Emphasis">
                    {BHARAT_MANDAPAM_TEXTS.COLLECT_YOUR_ORDER_FROM_HERE}
                </ConfirmSubheading>
            </ConfirmTitleContainer>

            <PickupPointCard>
                <PickupPointInfoCenter>
                    <PickupPointNameRow>
                        <Text variant="V2.heading2.neutral.black" color="text_Highest_Emphasis">
                            {pickupPointName}
                        </Text>
                        <NearestBadge variant="V2.caption.neutral.bold">
                            {BHARAT_MANDAPAM_TEXTS.NEAREST_BADGE}
                        </NearestBadge>
                    </PickupPointNameRow>
                    {/* {pickupPointAddress ? (
                        <Text variant="V2.body2.neutral.medium" color="text_High_Emphasis">
                            {pickupPointAddress}
                        </Text>
                    ) : null} */}
                </PickupPointInfoCenter>
            </PickupPointCard>

            <ConfirmButtonContainer>
                <FullWidthSuperButton
                    onClick={onChangePickupPoint}
                    buttonType="secondary"
                    buttonSize="large"
                    variant="fullwidth"
                    role="button"
                >
                    {BHARAT_MANDAPAM_TEXTS.CHANGE}
                </FullWidthSuperButton>

                <FullWidthSuperButton
                    onClick={onConfirm}
                    buttonType="primary"
                    buttonSize="large"
                    variant="fullwidth"
                    role="button"
                >
                    {BHARAT_MANDAPAM_TEXTS.CONFIRM_CTA}
                </FullWidthSuperButton>
            </ConfirmButtonContainer>

            {/* <ChangePickupLink variant="V2.body1.neutral.bold" color="special_primary" onClick={onChangePickupPoint}>
                {BHARAT_MANDAPAM_TEXTS.CHANGE_PICKUP_POINT}
                <RightChevronWrapper>
                    <GenericIcon Icon={ArrowRight} color="special_primary" fillColor="special_primary" />
                </RightChevronWrapper>
            </ChangePickupLink> */}
        </ConfirmContainer>
    );
});

function BharatMandapamConfirmPickup(props: BharatMandapamConfirmPickupProps): ReactElement {
    const { isOpen, onClose, ...contentProps } = props;

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    return (
        <HalfCard
            isOpen={isOpen}
            onClose={handleClose}
            closeOnOverlayClick
            hasCustomPadding={true}
            positionFromBottom={getPositionFromBottomForAddressList()}
        >
            <BharatMandapamConfirmPickupContent {...contentProps} />
        </HalfCard>
    );
}

export default memo(BharatMandapamConfirmPickup);
